export type GeolocationButtonPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface GeolocationButtonOptions {
  /** Texte de l'infobulle et libellé d'accessibilité. Par défaut : "Recentrer sur ma position". */
  label?: string;
  /** Coin de l'écran où placer le bouton. Par défaut : "bottom-right", au-dessus de `ZoomControl`
   * (voir `stackOffsetPx`). */
  position?: GeolocationButtonPosition;
  /**
   * Fusionné par-dessus le style par défaut (position, apparence) — même convention que
   * `ResetViewButtonOptions.style` (voir son commentaire).
   */
  style?: Partial<CSSStyleDeclaration>;
}

const POSITION_OFFSETS: Record<GeolocationButtonPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

// Même apparence que `ResetViewButton`/`ZoomControl` — les trois s'empilent dans le même coin (voir
// `createTokaMap.ts`), ils doivent donc se ressembler.
const DEFAULT_STYLE: Partial<CSSStyleDeclaration> = {
  // Relatif au `container` passé à `createTokaMap` (rendu positionné pour l'occasion si besoin,
  // voir `createTokaMap.ts`), pas à la page entière — même convention que `ResetViewButton`.
  position: "absolute",
  zIndex: "10",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  padding: "0",
  background: "#fff",
  color: "#444",
  border: "1px solid #ddd",
  // Carré arrondi, pas un cercle : s'empile avec le bloc à deux boutons de `ZoomControl`, qui ne
  // peut pas être rond — une colonne mêlant les deux formes ne se lit pas comme un ensemble.
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  cursor: "pointer",
};

/**
 * Bouton "recentrer la carte sur ma position" (voir BESOINS.md §15) — masqué tant que la
 * géolocalisation est désactivée ou que le point de position courante reste visible à l'écran (voir
 * `setVisible`, piloté par `createTokaMap` via `"geolocation:changed"`/`"geolocation:visibility"`) :
 * inutile de l'afficher quand il n'y a rien à recentrer, ou que la position est déjà dans le cadrage.
 */
export class GeolocationButton {
  private el: HTMLButtonElement;
  /** Valeur de `display` à utiliser quand le bouton est visible (celle issue de `DEFAULT_STYLE`,
   * éventuellement remplacée par `options.style.display`) — même mécanisme que
   * `ResetViewButton.visibleDisplay` (voir son commentaire) : sans ça, `setVisible(true)` retomberait
   * sur le `display` par défaut du navigateur pour un `<button>`, pas sur le `"flex"` nécessaire au
   * centrage de l'icône. */
  private visibleDisplay: string;
  /** Bord (`"top"` ou `"bottom"`) sur lequel `setStackOffset` agit — dérivé du coin résolu
   * (`options.position`), pour rester cohérent avec le côté que `POSITION_OFFSETS` a réellement
   * positionné (un coin `"bottom-*"` décale depuis le bas, pas depuis le haut). */
  private edgeProperty: "top" | "bottom";

  constructor(container: HTMLElement, options: GeolocationButtonOptions, onRecenter: () => void) {
    this.el = document.createElement("button");
    this.el.type = "button";
    this.el.className = "toka-geolocation-button";
    this.el.appendChild(createGeolocationIcon());
    const label = options.label ?? "Recentrer sur ma position";
    this.el.setAttribute("aria-label", label);
    // `title` en plus d'`aria-label` : sans lui, aucune infobulle au survol — l'icône seule ne dit
    // pas ce que fait le bouton à qui le découvre.
    this.el.title = label;
    Object.assign(this.el.style, DEFAULT_STYLE, POSITION_OFFSETS[options.position ?? "bottom-right"], options.style);
    this.visibleDisplay = this.el.style.display;
    this.edgeProperty = (options.position ?? "bottom-right").startsWith("top") ? "top" : "bottom";
    this.el.addEventListener("click", onRecenter);
    container.appendChild(this.el);
    // Masqué tant que `createTokaMap` n'a pas reçu de `"geolocation:visibility"`/`"geolocation:changed"`
    // confirmant qu'il y a réellement quelque chose à recentrer (voir `setVisible`) — après
    // `Object.assign` ci-dessus pour ne pas dépendre de l'ordre des clés dans `options.style`.
    this.setVisible(false);
  }

  setVisible(visible: boolean): void {
    this.el.style.display = visible ? this.visibleDisplay : "none";
  }

  /**
   * Réajuste le décalage (px, depuis le bord donné par `edgeProperty`) — appelé par `createTokaMap`
   * chaque fois que la visibilité de `ResetViewButton` change, pour que ce bouton reprenne
   * immédiatement la place laissée libre quand `ResetViewButton` disparaît (plutôt que de garder un
   * décalage figé calculé une seule fois à la création, qui laisserait alors un vide). Écrit
   * directement l'inline style — pas fusionné avec `options.style`, qui n'a plus d'effet sur cette
   * propriété une fois cette méthode appelée au moins une fois.
   */
  setStackOffset(offsetPx: number): void {
    this.el.style[this.edgeProperty] = `${offsetPx}px`;
  }

  destroy(): void {
    this.el.remove();
  }
}

/** Icône "cible" (repérage de position) — même style (trait `currentColor`, pas de remplissage) que
 * `ResetViewButton`/`SearchBox`. */
function createGeolocationIcon(): SVGSVGElement {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const circle = document.createElementNS(SVG_NS, "circle");
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "12");
  circle.setAttribute("r", "7");
  svg.appendChild(circle);
  const ticks = [
    ["12", "1", "12", "4"],
    ["12", "20", "12", "23"],
    ["1", "12", "4", "12"],
    ["20", "12", "23", "12"],
  ];
  for (const [x1, y1, x2, y2] of ticks) {
    const line = document.createElementNS(SVG_NS, "line");
    line.setAttribute("x1", x1!);
    line.setAttribute("y1", y1!);
    line.setAttribute("x2", x2!);
    line.setAttribute("y2", y2!);
    svg.appendChild(line);
  }
  return svg;
}
